import { Component, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { PopoverModule, Popover } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { Feed } from '@suprsend/web-sdk';
import type { IRemoteNotification, IFeedData } from '@suprsend/web-sdk';

import { SuprsendService } from '@/core/services/suprsend.service';
import { ApprovalPendingService } from '@/modules/approvals/services/approval-pending.service';
import { PendingApprovalStep } from '@/modules/settings/components/approval-engine/models/approval-engine.models';
import { approvalEntityLabel } from '@/modules/approvals/approval-entity-types';

/**
 * Campana unificada del banner. Fusiona dos fuentes en un único panel (p-popover):
 *  1. Aprobaciones pendientes (motor de aprobaciones, datos locales) — cada una con deep-link a
 *     /approvals/review/:approvalStepId.
 *  2. Notificaciones generales de SuprSend — leídas vía el SDK Feed (no el web-component Inbox,
 *     que queda oculto en el topbar). Ver suprsend.service.ts getFeedInstance().
 * El badge suma ambas fuentes (aprobaciones + no vistas de SuprSend).
 */
@Component({
    selector: 'app-notifications-bell',
    standalone: true,
    imports: [CommonModule, RouterModule, PopoverModule, ButtonModule, TooltipModule, TagModule],
    templateUrl: './notifications-bell.component.html',
    styleUrls: ['./notifications-bell.component.scss']
})
export class NotificationsBellComponent implements OnInit, OnDestroy {
    private readonly suprsend = inject(SuprsendService);
    private readonly approvals = inject(ApprovalPendingService);
    private readonly router = inject(Router);

    @ViewChild('op') op!: Popover;

    /** Aprobaciones pendientes (signal ya existente del servicio). */
    pendingSteps = this.approvals.steps;
    approvalCount = this.approvals.totalCount;

    /** Notificaciones SuprSend traídas por el SDK Feed. */
    suprsendItems = signal<IRemoteNotification[]>([]);
    suprsendUnseen = signal<number>(0);

    /** Badge combinado. */
    totalBadge = computed(() => this.approvalCount() + this.suprsendUnseen());

    private feed: Feed | null = null;
    private initSub?: Subscription;
    private storeHandler?: (data: IFeedData) => void;

    ngOnInit(): void {
        // El feed solo existe una vez que SuprSend terminó de inicializar (tras cargar el perfil).
        this.initSub = this.suprsend.isInitialized$.subscribe((ready) => {
            if (ready && !this.feed) this.attachFeed();
        });
    }

    ngOnDestroy(): void {
        this.initSub?.unsubscribe();
        if (this.feed && this.storeHandler) {
            this.feed.emitter.off('feed.store_update', this.storeHandler);
        }
    }

    private attachFeed(): void {
        try {
            this.feed = this.suprsend.getFeedInstance();
            if (!this.feed) return;

            this.storeHandler = (data: IFeedData) => {
                const list = data?.notifications ?? [];
                this.suprsendItems.set(list);
                this.suprsendUnseen.set(list.filter((n) => !n.seen_on).length);
            };
            this.feed.emitter.on('feed.store_update', this.storeHandler);

            try {
                this.feed.initializeSocketConnection();
            } catch {
                /* real-time opcional */
            }
            this.feed.fetch().catch(() => {
                /* silencioso: el badge de aprobaciones sigue vivo */
            });
        } catch {
            this.feed = null;
        }
    }

    toggle(event: Event): void {
        // Refrescar al abrir: el refresh de ApprovalPendingService corre una sola vez al arrancar
        // la app (cuando el token MSAL puede no estar listo aún) y luego cada 60s. Sin este
        // refresh, abrir la campana poco después de cargar mostraba la sección de aprobaciones vacía.
        this.approvals.refresh();
        try {
            this.feed?.fetch();
        } catch {
            /* el badge de aprobaciones sigue vivo */
        }
        this.op.toggle(event);
    }

    // ── Aprobaciones ─────────────────────────────────────────────────────────
    // El mapa de nombres vive en el módulo de aprobaciones: acá había una segunda copia, y al sumar
    // un EntityType nuevo la campana quedaba mostrando el valor crudo mientras la bandeja no.
    entityLabel(entityType: string): string {
        return approvalEntityLabel(entityType);
    }

    stepSubtitle(step: PendingApprovalStep): string {
        const parts: string[] = [];
        // El código de Concreto primero: es lo que identifica el documento para quien lo va a firmar.
        if (step.targetEntityCode) parts.push(step.targetEntityCode);
        if (step.approverPositionName) parts.push(step.approverPositionName);
        if (step.stepOrder != null) parts.push(`Paso ${step.stepOrder}`);
        return parts.join(' · ');
    }

    // ── SuprSend ─────────────────────────────────────────────────────────────
    notificationTitle(n: IRemoteNotification): string {
        return n.message?.header || n.message?.text || 'Notificación';
    }

    notificationBody(n: IRemoteNotification): string {
        return n.message?.header ? (n.message?.text ?? '') : '';
    }

    onSuprsendClick(n: IRemoteNotification): void {
        try {
            this.feed?.markAsRead(n.n_id);
        } catch {
            /* no bloqueante */
        }

        // Feedback inmediato: marcar como vista/leída localmente (quita el punto de no-visto y
        // baja el badge) aunque la notificación no tenga link.
        const now = Date.now();
        this.suprsendItems.update((list) => list.map((x) => (x.n_id === n.n_id ? { ...x, seen_on: x.seen_on ?? now, read_on: x.read_on ?? now } : x)));
        this.suprsendUnseen.set(this.suprsendItems().filter((x) => !x.seen_on).length);

        let url = n.message?.url || n.message?.actions?.[0]?.url || (n.message as any)?.data?.actionUrl || (n as any)?.message?.actionUrl;

        // Fallback robusto: si la plantilla de SuprSend en producción no expone la variable URL, 
        // pero el texto dice "aprobación", lo forzamos a ir a la bandeja de pendientes.
        if (!url) {
            const header = n.message?.header?.toLowerCase() || '';
            const text = n.message?.text?.toLowerCase() || '';
            if (header.includes('aprobación') || header.includes('aprobacion') || text.includes('aprobación') || text.includes('aprobacion')) {
                url = '/approvals/pending';
            }
        }

        if (url) {
            // Normalizar URLs absolutas apuntando al dominio de producción para convertirlas en
            // rutas locales. Así, probar en localhost o develop no te expulsa de la SPA.
            if (url.includes('sym70.semaica.com')) {
                try {
                    const parsed = new URL(url);
                    url = parsed.pathname + parsed.search + parsed.hash;
                } catch {
                    // ignorar y seguir con la url original
                }
            }

            if (/^https?:\/\//i.test(url)) {
                window.open(url, n.message?.open_in_new_tab ? '_blank' : '_self');
            } else {
                this.op.hide();
                this.router.navigateByUrl(url);
            }
        }
    }

    markAllRead(): void {
        try {
            this.feed?.markAllAsRead();
        } catch {
            /* no bloqueante */
        }
    }

    /** Elimina (archiva) una notificación de SuprSend para que no se acumule. */
    archive(n: IRemoteNotification, event: Event): void {
        event.stopPropagation(); // no disparar el click del ítem
        try {
            this.feed?.markAsArchived(n.n_id);
        } catch {
            /* no bloqueante */
        }
        // Quita optimista; el evento store_update terminará de reconciliar.
        this.suprsendItems.update((list) => list.filter((x) => x.n_id !== n.n_id));
        this.suprsendUnseen.set(this.suprsendItems().filter((x) => !x.seen_on).length);
    }

    trackByStep = (_: number, s: PendingApprovalStep) => s.approvalStepId;
    trackByNotif = (_: number, n: IRemoteNotification) => n.n_id;
}
